/**
 * 모집단 폭포 (DESIGN §3.6) — **P3 에 상시 노출**.
 *
 * "52만기 중 무엇을 왜 뺐는가"를 먼저 보여주지 않으면 다른 페이지의 어떤 숫자도
 * 해석할 수 없다. `kind='split'` 은 제외가 아니라 구분이므로 최종 N 을 줄이지 않는다.
 *
 * 막대는 **직전 단계의 잔여**를 폭으로 하고 깎인 만큼을 붉은 꼬리로 그린다 —
 * 각 단계가 얼마나 잘라냈는지가 길이로 바로 읽힌다.
 */

import { useQuery } from '@tanstack/react-query'

import { ChartFrame } from '@/components/ChartFrame'
import { Headline } from '@/components/WidgetShell'
import { fetchEnvelope } from '@/lib/api'
import type { Envelope, WaterfallStep } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'
import './PopulationWaterfall.css'

interface Data {
  steps: WaterfallStep[]
}

export function PopulationWaterfall() {
  const filters = useFilters()
  const q = useQuery({
    queryKey: ['quality/waterfall', filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<Data>('/quality/waterfall', toQueryFilters(filters), {}, signal),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<Data> | undefined
  const steps = env?.data.steps ?? []
  const total = steps[0]?.n_remaining ?? 0
  const excludes = steps.filter((s) => s.kind === 'exclude')
  const splits = steps.filter((s) => s.kind === 'split')
  const finalN = excludes.at(-1)?.n_remaining ?? total
  const droppedShare = total > 0 ? (total - finalN) / total : 0

  return (
    <ChartFrame
      title="모집단 폭포"
      question="52만기 중 무엇을 왜 뺐는가"
      meta={env?.meta}
      hideCoverage
      headline={
        <Headline
          value={finalN.toLocaleString('ko-KR')}
          unit={`기 · ${(droppedShare * 100).toFixed(1)}% 제외`}
        />
      }
      reading="전량 스냅샷에서 시작해 분석 모집단이 만들어지는 과정을 위에서 아래로 한 단계씩 보여줍니다. 각 줄이 한 번의 걸러내기이고, 맨 아래 숫자가 이 대시보드의 모든 수치가 쓰는 모집단입니다."
      encoding={[
        {
          role: '가로 막대',
          what: '그 단계가 끝난 뒤 남은 충전기 수',
          detail:
            '가장 위 막대(전량)를 100% 로 두고 비례합니다. 막대가 짧아지는 만큼이 걸러진 몫입니다.',
        },
        {
          role: '붉은 꼬리',
          what: '이 단계에서 깎인 수',
          detail: '막대 오른쪽 끝의 사선 부분입니다. 옆의 음수가 그 수입니다.',
        },
        {
          role: '연한 칸',
          what: '구분(split) — 빼지 않고 따로 센 몫',
          detail:
            "이용 제한(limitYn='Y')처럼 모집단에서 제외하지 않고 '이용자 관점 모드에서만 DOWN 으로 세는' 구분입니다.",
        },
        { role: '세로 순서', what: '걸러내는 순서', detail: '위에서 아래로 순차 적용됩니다.' },
      ]}
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={steps.length === 0}
      onRetry={() => void q.refetch()}
      notes={[
        {
          label: '구분(split)은 제외가 아니다.',
          text: '최종 N 을 줄이지 않고 별도로 셉니다. 그래서 마지막 줄의 숫자가 그 위 줄과 같습니다.',
        },
        {
          label: '이 최종 N 이 화면 전체의 기준이다.',
          text: '모든 API 응답의 meta.population_n 으로 전파됩니다 (§3.6). 어느 페이지의 어떤 숫자를 보든 같은 모집단입니다.',
        },
        {
          label: '왜 이 위젯이 첫 번째인가.',
          text: '"52만기의 가동률"과 "46만기의 가동률"은 다른 문장입니다. 무엇을 왜 뺐는지 모른 채 비율을 읽으면 오독합니다.',
        },
      ]}
    >
      <ol className="wf">
        {steps.map((s, i) => {
          const prev = i === 0 ? total : (steps[i - 1]?.n_remaining ?? total)
          const isSplit = s.kind === 'split'
          const isStart = s.kind === 'start'
          const base = total > 0 ? prev / total : 0
          const keep = total > 0 ? s.n_remaining / total : 0
          const cut = Math.max(0, base - keep)
          const splitShare = total > 0 ? Math.min(keep, s.n_affected / total) : 0

          return (
            <li key={s.step_index} className={`wf__row${isSplit ? ' wf__row--split' : ''}`}>
              <div className="wf__label">
                <span className="wf__name">{s.label}</span>
                {s.reason && <span className="wf__reason">{s.reason}</span>}
              </div>

              <div className="wf__barwrap">
              <div className="wf__bar" aria-hidden="true">
                {isSplit ? (
                  /* 구분은 잔여를 줄이지 않는다 — 같은 막대를 둘로 나눠 칠한다 */
                  <>
                    <span
                      className="wf__seg wf__seg--keep"
                      style={{ width: `${Math.max(0, keep - splitShare) * 100}%` }}
                    />
                    <span
                      className="wf__seg wf__seg--split"
                      style={{ width: `${splitShare * 100}%` }}
                    />
                  </>
                ) : (
                  <>
                    <span
                      className={`wf__seg ${isStart ? 'wf__seg--total' : 'wf__seg--keep'}`}
                      style={{ width: `${keep * 100}%` }}
                    />
                    {cut > 0 && (
                      <span
                        className="wf__seg wf__seg--cut hatch-down"
                        style={{ width: `${Math.max(0.6, cut * 100)}%` }}
                      />
                    )}
                  </>
                )}
              </div>
                {/* 부분 수치는 **막대 아래**에 둔다. 오른쪽에 두면 글자 수가
                    행마다 달라 3열 폭이 흔들리고, 그 탓에 막대 시작점이
                    행마다 어긋난다 (실측 20px 편차). */}
                {s.n_affected > 0 && (
                  <span className={isSplit ? 'wf__split num' : 'wf__minus num'}>
                    {isSplit ? '구분 ' : '−'}
                    {s.n_affected.toLocaleString('ko-KR')}
                  </span>
                )}
              </div>

              <span className="wf__remain num">{s.n_remaining.toLocaleString('ko-KR')}</span>
            </li>
          )
        })}
      </ol>

      <p className="wf__final">
        최종 분석 모집단 <strong className="num">{finalN.toLocaleString('ko-KR')}</strong>기
        {splits.length > 0 && (
          <span className="wf__finalnote">
            {' — '}
            그중{' '}
            {splits
              .map((s) => `${s.label} ${s.n_affected.toLocaleString('ko-KR')}`)
              .join(' · ')}
          </span>
        )}
      </p>
    </ChartFrame>
  )
}
